import React from 'react';
import { makeStyles, Text, Spinner, Button, tokens } from '@fluentui/react-components';
import { ChevronLeftRegular, ChevronRightRegular } from '@fluentui/react-icons';
import { SiteStatusCard } from './SiteStatusCard';
import { SiteClockStatus } from '../types';

const useStyles = makeStyles({
  container: {
    padding: '24px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(10, 1fr)',
    gap: '8px',
    marginBottom: '24px',
    '@media (max-width: 600px)': {
      gridTemplateColumns: 'repeat(3, 1fr)',
    },
    '@media (min-width: 601px) and (max-width: 900px)': {
      gridTemplateColumns: 'repeat(5, 1fr)',
    },
    '@media (min-width: 901px) and (max-width: 1200px)': {
      gridTemplateColumns: 'repeat(8, 1fr)',
    },
    '@media (min-width: 1201px)': {
      gridTemplateColumns: 'repeat(10, 1fr)',
    },
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '80px 20px',
    flexDirection: 'column',
    gap: '16px',
  },
  emptyState: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '80px 20px',
    flexDirection: 'column',
    gap: '12px',
    color: tokens.colorNeutralForeground3,
  },
  emptyTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground2,
  },
  emptyMessage: {
    fontSize: '14px',
    textAlign: 'center',
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '16px',
    paddingTop: '20px',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  pageInfo: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground2,
    minWidth: '120px',
    textAlign: 'center',
  },
  navButton: {
    minWidth: '40px',
  },
});

interface SiteStatusGridProps {
  sites: SiteClockStatus[];
  loading: boolean;
  onSiteClick: (site: SiteClockStatus) => void;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export const SiteStatusGrid: React.FC<SiteStatusGridProps> = ({
  sites,
  loading,
  onSiteClick,
  currentPage,
  totalPages,
  onPageChange,
}) => {
  const styles = useStyles();

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <Spinner size="large" />
          <Text>Loading sites...</Text>
        </div>
      </div>
    );
  }

  if (!sites || sites.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <Text className={styles.emptyTitle}>No Sites Found</Text>
          <Text className={styles.emptyMessage}>
            There are no sites matching your current filters.
            <br />
            Try adjusting your filters or date selection.
          </Text>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.grid}>
        {sites.map(site => (
          <SiteStatusCard
            key={`${site.site_id}-${site.shift_id}`}
            site={site}
            onClick={() => onSiteClick(site)}
          />
        ))}
      </div>

      {totalPages > 1 && (
        <div className={styles.pagination}>
          <Button
            appearance="subtle"
            icon={<ChevronLeftRegular />}
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={styles.navButton}
          />

          <Text className={styles.pageInfo}>
            Page {currentPage} of {totalPages}
          </Text>

          <Button
            appearance="subtle"
            icon={<ChevronRightRegular />}
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={styles.navButton}
          />
        </div>
      )}
    </div>
  );
};
